import * as React from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { WithStyles } from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import Config from 'src/site.config.json';
import { isMobile } from 'src/utils/MobileCheck';
import * as Actions from '../actions/eLearning';

import ScenarioFactory, {
	IEndELearningScormParam,
	IFetchELearningScormParam,
} from 'src/scenario/ScormScenarioFactory';
import { clearScormParams } from '../actions/scorm';
import { IRootState } from '../reducers';
import i18n from '../resources/i18n';

import { IELearningCommonProps } from './ELearning';

import 'src/logic/scorm/apiFrame';

/**
 * for styled component
 * @param theme
 */
const styles = (theme: Theme) =>
	createStyles({
		frame: {
			margin: 'auto',
			overflow: 'auto !important',
			paddingTop: theme.spacing(1),
			width: '100%',
			['-webkit-overflow-scrolling']: 'touch !important',
		},
	});

/**
 * eラーニングSCORM
 */
export class ELearningScormComponent extends React.Component<IProps> {
	protected elmIframe: React.RefObject<HTMLIFrameElement>;
	protected elmParent: React.RefObject<HTMLDivElement>;
	protected elmParentIframe: React.RefObject<HTMLIFrameElement>;
	protected resizeFrameWrapper: () => void;

	public constructor(props: IProps) {
		super(props);

		this.elmIframe = React.createRef();
		this.elmParent = React.createRef();
		this.elmParentIframe = React.createRef();
	}

	/**
	 * Lifecycle Methods
	 */
	public componentDidMount() {
		this.props.setScormApi(this.elmParentIframe);
		if (!this.props.currentTreeItem || this.props.outOfPeriodFlg) {
			return;
		}

		this.props.fetchELearningScorm(this.props.token, {
			careerID: this.props.careerID,
			classID: this.props.classID,
			isPreview: this.props.preview,
			itemID: this.props.currentTreeItem.id.substring(2),
			scoType: this.props.currentTreeItem.scoType as string,
			seqNo: this.props.currentTreeItem.seqNo,
			userId: this.props.userAttr.userId,
		});
	}

	/**
	 * Lifecycle Methods
	 */
	public componentWillUpdate(nextProps: IProps, nextState: IProps) {
		this.props.setScormApi(this.elmParentIframe);
		if (!this.props.currentTreeItem) {
			return;
		}
		if (
			nextProps.currentTreeItem &&
			this.props.currentTreeItem.id === nextProps.currentTreeItem.id
		) {
			return;
		}

		// 別のテキストに移動しようとしているため現在のテキストは受講終了
		this.endELearningScorm();
	}

	/**
	 * Lifecycle Methods
	 */
	public componentDidUpdate(prevProps: IProps) {
		this.props.setScormApi(this.elmParentIframe);
		if (!prevProps.currentTreeItem || !this.props.currentTreeItem) {
			return;
		}
		if (this.props.outOfPeriodFlg) {
			return;
		}

		if (!prevProps.showFinishDialog && this.props.showFinishDialog) {
			// 受講終了
			this.endELearningScorm();
		}

		if (prevProps.currentTreeItem.id !== this.props.currentTreeItem.id) {
			this.endELearningScorm();
			this.props.fetchELearningScorm(this.props.token, {
				careerID: this.props.careerID,
				classID: this.props.classID,
				isPreview: this.props.preview,
				itemID: this.props.currentTreeItem.id.substring(2),
				scoType: this.props.currentTreeItem.scoType as string,
				seqNo: this.props.currentTreeItem.seqNo,
				userId: this.props.userAttr.userId,
			});
			return;
		}
		if (prevProps.location !== this.props.location) {
			// locationが設定された
			if (
				!this.elmIframe.current ||
				!this.elmIframe.current.contentDocument
			) {
				return;
			}

			// SCORMのフレームに渡すデータの設定
			const apiFrame = window.API;
			apiFrame.initialize({
				careerID: this.props.careerID,
				itemID: this.props.currentTreeItem.id.substring(2),
				loginID: this.props.loginID,
				page: this.props.currentTreeItem.page,
				scoType: this.props.currentTreeItem.scoType,
				userId: this.props.userAttr.userId,
			});
			this.props.updateProgress();

			// SCORM読み込み
			const { careerID, location, preview } = this.props;
			const itemID = this.props.currentTreeItem.id.substring(2);
			let url = Config.tsqPath + '/tsq/pkg/action/el/contents/';
			url =
				url +
				`${careerID}&${preview ? '1' : '0'}&${itemID}/${location}`;
			this.elmIframe.current.contentDocument.location.replace(url);
			if (!this.elmIframe.current.onload) {
				this.elmIframe.current.onload = () => {
					this.resizeFrame();
					if (!this.resizeFrameWrapper) {
						this.resizeFrameWrapper = () => {
							this.resizeFrame();
						};
						const type = isMobile()
							? 'orientationchange'
							: 'resize';
						window.addEventListener(type, this.resizeFrameWrapper);
					}
				};
			}
		}
	}

	/**
	 * Lifecycle Methods
	 */
	public componentWillUnmount() {
		if (this.resizeFrameWrapper) {
			window.removeEventListener('resize', this.resizeFrameWrapper);
		}

		// 受講終了
		this.endELearningScorm();

		this.props.clearScormParams();
	}

	/**
	 * レンダリング
	 */
	public render() {
		const { classes } = this.props;

		if (!this.props.currentTreeItem) {
			return;
		}

		if (this.props.outOfPeriodFlg) {
			return <React.Fragment />;
		}

		return (
			<div className={classes.frame} ref={this.elmParent}>
				<iframe
					ref={this.elmIframe}
					frameBorder="0"
					title={i18n.t('ELearningScorm_0001')}
					name={i18n.t('ELearningScorm_0002')}
					width="800"
					height="600"
				/>
			</div>
		);
	}

	/**
	 * SCORM終了処理
	 */
	protected endELearningScorm() {
		if (this.elmIframe.current && this.elmIframe.current.contentWindow) {
			this.elmIframe.current.src = '';
		}

		if (!this.props.currentTreeItem) {
			return;
		}
		// 受講終了
		this.props.endELearningScorm(this.props.token, {
			careerID: this.props.careerID,
			classID: this.props.classID,
			isPreview: this.props.preview,
			itemID: this.props.currentTreeItem.id.substring(2),
			page: this.props.currentTreeItem.page,
			pageCount: this.props.currentTreeItem.pageCount,
			scoType: this.props.currentTreeItem.scoType as string,
			seqNo: this.props.currentTreeItem.seqNo,
			startTime: this.props.startTime,
			userId: this.props.userAttr.userId,
		});
	}

	/**
	 * リサイズハンドラ
	 */
	protected resizeFrame() {
		const innerHeight =
			window.innerHeight || document.documentElement.clientHeight;
		if (!(this.elmParent.current && this.elmIframe.current)) {
			return;
		}
		const rect = this.elmIframe.current.getBoundingClientRect();
		this.elmIframe.current.width = this.elmParent.current.offsetWidth.toString();
		this.elmIframe.current.height = (
			innerHeight -
			(rect.top - window.pageYOffset) -
			8
		).toString();
	}
}

/**
 * state -> props
 * @param state
 */
export const mapStateToProps = (state: IRootState) => ({
	location: state.scorm.location,
	startTime: state.scorm.startTime,

	currentTreeItem: state.eLearning.currentTreeItem,
	showFinishDialog: state.eLearning.showFinishDialog,

	loginID: state.session.loginID,
	token: state.session.token,
	userAttr: state.session.userAttr,
});

/**
 * disatch -> props
 * @param dispatch
 */
export const mapDispatchToProps = (dispatch: Dispatch) => ({
	clearScormParams: () => {
		dispatch(clearScormParams());
	},
	endELearningScorm: (token: string, arg: IEndELearningScormParam) => {
		const s = ScenarioFactory.createEndELearningScorm(dispatch, token);
		s.run(arg);
	},
	fetchELearningScorm: (token: string, arg: IFetchELearningScormParam) => {
		const s = ScenarioFactory.createFetchELearningScorm(dispatch, token);
		s.run(arg);
	},

	setScormApi: (scormApi: React.RefObject<HTMLIFrameElement>) => {
		dispatch(Actions.setScormApi({ scormApi }));
	},
});

/**
 * ELearningScormで受け取るProps型
 */
export interface IELearningScormProps extends IELearningCommonProps {
	updateProgress: () => Promise<void>;
}

/**
 * connect用マージ関数
 * @param state
 * @param events
 * @param ownProps
 */
const mergeProps = (
	state: IStateProps,
	events: IDispatchProps,
	ownProps: IOwnProps
) => ({
	...state,
	...events,
	...ownProps,
});

/** state-props型 */
type IStateProps = ReturnType<typeof mapStateToProps>;
/** dispatch-props型 */
type IDispatchProps = ReturnType<typeof mapDispatchToProps>;
/** 公開Props型 + Styled Component Props */
type IOwnProps = WithStyles<typeof styles> & IELearningScormProps;
/** 内部用Props型 */
type IProps = ReturnType<typeof mergeProps>;

/**
 * connect済 ELearningScormComponent
 */
const Connected = connect(
	mapStateToProps,
	mapDispatchToProps,
	mergeProps
)(ELearningScormComponent);

/**
 * SCORM用ELearningページコンポーネント
 */
const ELearningScorm = withStyles(styles)(Connected);

export default ELearningScorm;



// WEBPACK FOOTER //
// ./src/containers/ELearningScorm.tsx